import Card from '@mui/material/Card';
import Grid from '@mui/material/GridLegacy';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import classnames from 'classnames';
import type { PropsWithChildren } from 'react';
import React, { forwardRef } from 'react';

import CloseIcon from '../../icons/Actions/CloseIcon';
import CheckIcon from '../../icons/Alerts/CheckIcon';
import ErrorIcon from '../../icons/Alerts/ErrorIcon';
import InfoIcon from '../../icons/Alerts/InfoIcon';
import WarningIcon from '../../icons/Alerts/WarningIcon';
import { withStyles } from '../../styles/tools';
import { withTrace } from '../../utils';
import { styles, useStyles } from './Notification.styles';
import type { IconProps, NotificationProps } from './Notification.types';
import { NotificationType } from './Notification.types';

export const StatusIcon = ({ type }: IconProps) => {
    let retval = <InfoIcon />;
    switch (type) {
        case NotificationType.SUCCESS:
        case NotificationType.SUCCESS_LIGHT:
            retval = <CheckIcon />;
            break;
        case NotificationType.ERROR:
        case NotificationType.ERROR_LIGHT:
            retval = <ErrorIcon />;
            break;
        case NotificationType.WARNING:
        case NotificationType.WARNING_LIGHT:
            retval = <WarningIcon />;
    }
    return retval;
};

/**
 * Creates an appropriately styled icon. ie grey background vs filled vs info blue.
 * with height being centered if no children or top if children
 * @param props classes, type, children. Used in decisions about styling
 * @constructor Returns styled component
 */
export const IconComponent = (props: PropsWithChildren<IconProps>) => {
    const { classes, type, description, applyTrace, children } = props;

    const iconClasses = classnames(
        {
            [classes.infoBackground]: type === NotificationType.INFO_LIGHT,
            // INFO_LIGHT notifications have a light blue tinge to the background of the icon and message
        },
        classes.statusIcon,
        classes[type],
    );
    const iconFormat = classnames(
        {
            [classes.iconWrapperDescription]: description && !children,
            [classes.iconWrapperDescriptionChildren]: description && children,
        },
        classes.iconWrapper,
    );

    return (
        <Grid item className={iconClasses} {...applyTrace(`icon-${type}`)}>
            <Grid container className={iconFormat}>
                <StatusIcon {...props} />
            </Grid>
        </Grid>
    );
};

/**
 * Creates an appropriately styled message. ie grey background vs white.
 * With or without a description and thus message styling
 * @param props classes, message, description, type, applyTrace, closeCallback. Used in decisions about styling
 * @constructor Returns styled component
 */
export const MessageComponent = (
    props: PropsWithChildren<NotificationProps & { inlineChildren?: boolean }>,
) => {
    const {
        classes,
        message,
        messageComponent,
        description,
        type,
        applyTrace,
        closeCallback,
        children,
        inlineChildren = false,
    } = props;

    const messageStyle = classnames({
        // INFO_LIGHT notifications have a light blue tinge to the background of the icon and message
        [classes.infoBackground]: type === NotificationType.INFO_LIGHT && description,
        [classes.defaultBackground]: type !== NotificationType.INFO_LIGHT && description,
    });

    const descriptionStyle = classnames(
        {
            [classes.notificationMessageDescription]: description && !children,
            [classes.notificationMessageChildren]: description && children,
        },
        classes.notificationMessage,
    );

    const renderMessageAndInlineChildren = () => (
        <Grid container alignItems="center" style={{ height: '100%' }}>
            <Grid item xs>
                <Grid flexDirection="column">
                    <Typography
                        className={classes.contentText}
                        {...applyTrace('message')}
                        component={messageComponent ?? 'p'}
                    >
                        {message}
                    </Typography>
                    <div style={{ alignSelf: 'self-start' }}>{children}</div>
                </Grid>
            </Grid>
        </Grid>
    );

    const renderMessageAsHeading = () => (
        <Grid container alignItems="center">
            <Grid item xs>
                <Typography variant="h5" {...applyTrace('message')}>
                    {message}
                </Typography>
            </Grid>
        </Grid>
    );

    const renderMessage = () => (
        <Grid container alignItems="center" style={{ height: '100%' }}>
            <Grid item xs>
                <Typography
                    className={classes.contentText}
                    {...applyTrace('message')}
                    component={messageComponent ?? 'p'}
                >
                    {message}
                </Typography>
            </Grid>
        </Grid>
    );

    const renderContent = () => {
        if (inlineChildren && children) {
            return renderMessageAndInlineChildren();
        }
        if (description) {
            return renderMessageAsHeading();
        }
        return renderMessage();
    };

    const renderDescription = () => (
        <Grid container alignItems="center">
            <Grid item xs>
                <Typography variant="body2" {...applyTrace('description')}>
                    {description}
                </Typography>
            </Grid>
        </Grid>
    );

    const renderCloseButton = () => (
        <Grid item>
            <IconButton
                onClick={closeCallback}
                className={classes.closeButton}
                {...applyTrace('close-button')}
                size="large"
            >
                <CloseIcon fontSize="inherit" />
            </IconButton>
        </Grid>
    );

    return (
        <Grid container alignItems="stretch" wrap="nowrap" className={messageStyle}>
            <Grid xs item className={descriptionStyle}>
                {renderContent()}
                {description && renderDescription()}
            </Grid>
            {closeCallback && renderCloseButton()}
        </Grid>
    );
};

export const NotificationBase = forwardRef(
    (props: NotificationProps & { inlineChildren?: boolean }, ref: any) => {
        const { children, closeCallback = null, applyTrace, inlineChildren = false } = props;
        const { classes } = useStyles(undefined, { props: { classes: props.classes } });

        const propsForChildren = { ...props, classes, inlineChildren };

        const rootStyle = classnames(
            {
                [classes.boxShadow]: closeCallback !== null,
                [classes.border]: closeCallback === null,
            },
            classes.root,
        );

        return (
            <Card ref={ref} variant="outlined" square {...applyTrace('root')} className={rootStyle}>
                <Grid container alignItems="stretch" wrap="nowrap">
                    <IconComponent {...propsForChildren} />
                    <MessageComponent {...propsForChildren} />
                </Grid>
                {!inlineChildren && children && (
                    <Grid item className={classes.childrenStyle} {...applyTrace('children')}>
                        {children}
                    </Grid>
                )}
            </Card>
        );
    },
);
const traced = withTrace('notification')(NotificationBase);
export const Notification = withStyles(styles)(traced);
